import type { HttpErrorResponse } from '@angular/common/http';

import type {
  EEOCategories,
  EEOConfiguration,
  EEONoDataPlacements,
  EEOExportData,
  NoEEODataRequest,
} from '@recruitee/eeo-types';
import { createSingleEntityStore, pipeState, ngrxEvent } from '@recruitee/ngrx';

import type { EEOConfigurationFormData, EEOUpdateStatus } from '../eeo-data.types';

const {
  actions: EEOActions,
  namespace: EEONamespace,
  selectors: EEOSelectors,
  reducer: EEOReducer,
} = createSingleEntityStore<EEOConfiguration | null>()('EEO', {
  customActions: adapter => ({
    update: (state, payload: EEOConfigurationFormData) => adapter.markAsPending(state),
    updateSuccess: (state, payload: EEOConfiguration & { updated: EEOUpdateStatus }) =>
      pipeState(
        state,
        next =>
          adapter.save(next, {
            companySettings: payload.companySettings,
            offersWithDisabledEeoCount: payload.offersWithDisabledEeoCount,
          }),
        next => adapter.markAsLoaded(next),
      ),
    sendEmailForSingleCandidate: ngrxEvent<NoEEODataRequest>(),
    sendEmailForSingleCandidateSuccess: ngrxEvent<{ candidateId: number }>(),
    sendEmailForSingleCandidateFailed: (
      state,
      payload: { scope: string; error: HttpErrorResponse },
    ) => adapter.setError(state, payload),
    loadNoEEODataPlacements: ngrxEvent<{ candidateId: number }>(),
    loadNoEEODataPlacementsSuccess: ngrxEvent<EEONoDataPlacements>(),
    loadNoEEODataPlacementsFailed: (state, payload: { scope: string; error: HttpErrorResponse }) =>
      adapter.setError(state, payload),
    bulkSend: ngrxEvent<{ candidateIds: number[]; emailSubject: string; emailBodyHtml: string }>(),
    bulkSendSuccess: ngrxEvent<{ candidatesCount: number }>(),
    bulkSendFailed: (state, payload: { scope: string; error: HttpErrorResponse }) =>
      adapter.setError(state, payload),
    bulkResend: ngrxEvent<{
      candidateIds: number[];
      emailSubject: string;
      emailBodyHtml: string;
    }>(),
    bulkResendSuccess: ngrxEvent<{ candidatesCount: number }>(),
    bulkResendFailed: (state, payload: { scope: string; error: HttpErrorResponse }) =>
      adapter.setError(state, payload),
    exportEEOReport: ngrxEvent<EEOExportData>(),
    bulkEnable: ngrxEvent<{ offerIds: number[]; jobCategory: string }>(),
    bulkEnableSuccess: ngrxEvent<{ uuid: string }>(),
    bulkEnableFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
    bulkDisable: ngrxEvent<{ offerIds: number[] }>(),
    bulkDisableSuccess: ngrxEvent<{ uuid: string }>(),
    bulkDisableFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
  }),
});

const {
  actions: EEOCategoriesActions,
  namespace: EEOCategoriesNamespace,
  selectors: EEOCategoriesSelectors,
  reducer: EEOCategoriesReducer,
} = createSingleEntityStore<EEOCategories | null>()('EEOCategories');

export {
  EEOCategoriesActions,
  EEOCategoriesNamespace,
  EEOCategoriesSelectors,
  EEOCategoriesReducer,
};

export { EEOActions, EEONamespace, EEOSelectors, EEOReducer };
